import { CognitoService } from 'src/app/modules/core/services/cognito.service';
import { Router } from '@angular/router';
import { ExpireTokenException, Role, UnAuthorizedException, UserAttrs } from './../../types';
import { Injectable } from '@angular/core';
import * as AWS from 'aws-sdk';
import jwtDecode from 'jwt-decode';
import { Subject } from 'rxjs';
@Injectable({
    providedIn: 'root',
})
export class AuthService {
    s3: AWS.S3 = new AWS.S3();

    constructor(private router: Router, private cognitoService: CognitoService) {}

    onAuthentication(accessToken: string, idToken: string) {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('idToken', idToken);
    }

    userAttr: UserAttrs;

    updateProfileEvent = new Subject();

    get userAttrs(): UserAttrs {
        const idToken = this.getIdToken();
        const decodedIdToken = jwtDecode<any>(idToken);
        const userAttrs: UserAttrs = {
            sub: decodedIdToken.sub,
            role: decodedIdToken['custom:role'],
            email: decodedIdToken.email,
            emailVerified: decodedIdToken.email_verified,
            givenName: decodedIdToken.given_name,
            name: decodedIdToken.name,
            phoneNumber: decodedIdToken.phone_number,
            picture: decodedIdToken.picture,
        };

        this.userAttr = userAttrs;

        return userAttrs;
    }

    get role(): Role {
        return this.userAttrs.role;
    }

    async logout() {
        this.removeToken();
        await this.cognitoService.logout();
        this.router.navigate(['/login']);
    }

    get isAuthenticated(): boolean {
        try {
            const accessToken = this.getAccessToken();
            return !!accessToken;
        } catch (e) {
            return false;
        }
    }

    get isSuperAdmin() {
        return this.role === Role.SUPER_ADMIN;
    }

    get isOrgAdmin() {
        return this.role === Role.ORG_ADMIN;
    }

    get isSimuAdmin() {
        return this.role === Role.SIMU_ADMIN;
    }

    getAccessToken(): string {
        const accessToken = localStorage.getItem('accessToken');
        const currentTime = Math.floor(Date.now() / 1000); // get current time in seconds
        if (accessToken) {
            const { exp } = jwtDecode<any>(accessToken);
            if (exp < currentTime) {
                this.logout();
                throw new ExpireTokenException();
            }
            return accessToken;
        }
        throw new UnAuthorizedException();
    }

    getIdToken(): string {
        const idToken = localStorage.getItem('idToken');
        const currentTime = Math.floor(Date.now() / 1000); // get current time in seconds
        if (idToken) {
            const { exp } = jwtDecode<any>(idToken);
            if (exp < currentTime) {
                this.logout();
                throw new ExpireTokenException();
            }
            return idToken;
        }
        throw new UnAuthorizedException();
    }

    tryReLogin() {
        try {
            const accessToken = this.getAccessToken();
            const idToken = this.getIdToken();
            if (accessToken && idToken) {
                this.onAuthentication(accessToken, idToken);
            }
        } catch (e) {
            this.logout();
        }
    }

    public async fetchUserAttrs(): Promise<any> {
        this.cognitoService
            .getUser()
            .then(user => {
                const attrs = user.attributes;
                const userAttrs: UserAttrs = {
                    role: attrs['custom:role'],
                    email: attrs.email,
                    emailVerified: attrs.email_verified,
                    givenName: attrs.given_name,
                    name: attrs.name,
                    phoneNumber: attrs.phone_number,
                    picture: attrs.picture,
                    sub: attrs.sub,
                };
                return userAttrs;
            })
            .catch(() => {
                this.logout();
            });
    }

    public async currentSession() {
        return await this.cognitoService.currentCredentials();
    }

    private removeToken() {
        localStorage.removeItem('access_token');
        localStorage.removeItem('idToken');
    }

    emitUpdateUser() {
        this.updateProfileEvent.next();
    }
}
